import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { getMetaTrackingData } from "../utils/tracking";
import { track } from "../utils/track";
import { motion, AnimatePresence } from "framer-motion";
import {
  Zap,
  CreditCard,
  History,
  ArrowRight,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Calendar,
  Layers,
  ShoppingBag,
} from "lucide-react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export default function TokensPage() {
  const { t, i18n } = useTranslation();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [pricingData, setPricingData] = useState(null);
  const [tokensInfo, setTokensInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPack, setLoadingPack] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const language = i18n.language || "es";

  const loadData = useCallback(async () => {
    const fallbackPricing = {
      plans: {
        free: { name: { es: "Free", en: "Free" }, credits_per_month: 20 },
        basic: { name: { es: "Basic", en: "Basic" }, credits_per_month: 200 },
        standard: {
          name: { es: "Standard", en: "Standard" },
          credits_per_month: 1000,
        },
        business: {
          name: { es: "Business", en: "Business" },
          credits_per_month: 3500,
        },
        enterprise: {
          name: { es: "Enterprise", en: "Enterprise" },
          credits_per_month: 15000,
        },
      },
      additional_features: {
        credit_packs: {
          mini: { credits: 100, price_eur: 5 },
          small: { credits: 500, price_eur: 35 },
          medium: { credits: 2000, price_eur: 100 },
          large: { credits: 10000, price_eur: 350 },
        },
      },
    };

    try {
      setLoading(true);
      const [subscription, history, pricing, tokens] = await Promise.all([
        api.getSubscription().catch(() => null),
        api.getUsageHistory().catch(() => []),
        api.getSubscriptionPricing().catch(() => null),
        api.getTokens().catch(() => null),
      ]);

      const normalizedSubscription =
        subscription?.subscription &&
        typeof subscription.subscription === "object"
          ? subscription.subscription
          : subscription;

      setSubscriptionData(normalizedSubscription);
      setUsageHistory(history || []);
      setPricingData(pricing || fallbackPricing);
      setTokensInfo(tokens || null);
    } catch (err) {
      console.error("Error loading data:", err);
      setError(t("tokens.errorLoading", "Error loading data"));
      setPricingData(fallbackPricing);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!user) return;
    loadData();
  }, [user, loadData]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const purchaseStatus = urlParams.get("purchase");

    if (purchaseStatus === "success") {
      track("checkout_return", { result: "success", kind: "pack" });
      setSuccess(t("tokens.purchaseSuccess", "Credits added successfully!"));
      window.history.replaceState({}, document.title, window.location.pathname);
      if (refresh) refresh();
      loadData();
    }
  }, [t, refresh, loadData]);

  const handleBuyCreditPack = async (packKey) => {
    track("pack_click", { item: packKey });
    setError(null);
    setLoadingPack(packKey);

    try {
      const tracking = getMetaTrackingData();
      const { checkoutUrl } = await api.createCreditPackCheckout({
        packId: packKey,
        successUrl: `${window.location.origin}/tokens?purchase=success`,
        cancelUrl: `${window.location.origin}/tokens`,
        ...tracking,
      });
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(
        err.response?.data?.message ||
          t("tokens.errorPurchasing", "Purchase error")
      );
    } finally {
      setLoadingPack(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await api.getCustomerPortalUrl();
      const portalUrl = response?.portalUrl || response?.url;
      if (!portalUrl) {
        throw new Error("Missing portal URL");
      }
      window.location.href = portalUrl;
    } catch (err) {
      setError(t("tokens.errorPortal", "Error opening portal"));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString(
      language === "es" ? "es-ES" : "en-US",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
      }
    );
  };

  const getNextRenewalDate = (sub) => {
    if (!sub) return null;
    if (sub.next_billing_date) return sub.next_billing_date;
    if (!sub.last_credit_reset) return null;
    const d = new Date(sub.last_credit_reset);
    if (Number.isNaN(d.getTime())) return null;
    const next = new Date(d);
    next.setMonth(next.getMonth() + 1);
    return next.toISOString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const currentPlan =
    subscriptionData?.subscription_plan || user?.subscription_plan || "free";
  const subscriptionStatus = subscriptionData?.subscription_status || null;
  const isCancelingSubscription = subscriptionStatus === "canceling";
  const creditsBalance =
    subscriptionData?.credits_balance ??
    tokensInfo?.tokens_balance ??
    user?.credits_balance ??
    0;

  // Logic for monthly credits display
  const monthlyCredits =
    subscriptionData?.monthly_credit_limit ??
    pricingData?.plans?.[currentPlan]?.credits_per_month ??
    0;

  const monthlyRemaining = subscriptionData?.monthly_credit_limit
    ? Math.max(
        0,
        subscriptionData.monthly_credit_limit -
          (subscriptionData.credits_used_this_month || 0)
      )
    : tokensInfo?.monthly_remaining ?? 0;

  const packCredits =
    tokensInfo?.pack_credits_balance ??
    subscriptionData?.pack_credits_balance ??
    // Backward compatibility (older backends used extra_credits as packs)
    tokensInfo?.extra_credits ??
    subscriptionData?.extra_credits ??
    0;
  const planDetails = pricingData?.plans[currentPlan];

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 max-w-7xl mx-auto space-y-8 min-h-screen pb-20"
    >
      {/* Notifications */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> {error}
            </div>
            <button onClick={() => setError(null)} className="font-bold">
              &times;
            </button>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" /> {success}
            </div>
            <button onClick={() => setSuccess(null)} className="font-bold">
              &times;
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-2">
            {t("tokens.title", "Tokens & Billing")}
          </h1>
          <p className="text-gray-500 max-w-xl">{t("tokens.description")}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {currentPlan !== "free" && (
            <button
              onClick={handleManageSubscription}
              className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-5 py-2.5 rounded-xl font-bold text-sm shadow-sm transition-all"
            >
              {t("tokens.billingPortal", "Billing Portal")}
            </button>
          )}
          <button
            onClick={() => {
              track("upgrade_click", { from: "tokens_page" });
              navigate("/pricing");
            }}
            className="bg-gray-900 text-white hover:bg-black px-6 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-gray-900/20 transition-all hover:scale-105"
          >
            {currentPlan === "free"
              ? t("tokens.upgradePlan", "Upgrade Plan")
              : t("tokens.changePlan", "Change Plan")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Balance Card */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="lg:col-span-2 bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl"
        >
          <div className="absolute top-0 right-0 p-32 bg-primary-500/20 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row justify-between gap-8">
            <div>
              <div className="flex items-center gap-2 text-gray-400 font-bold uppercase tracking-wider text-xs mb-3">
                <Zap className="w-4 h-4 text-yellow-400" />{" "}
                {t("tokens.totalBalance")}
              </div>
              <div className="text-5xl sm:text-6xl font-extrabold tracking-tighter mb-4">
                {creditsBalance.toLocaleString()}
              </div>
              <div className="flex items-center gap-4 text-sm font-medium text-gray-400">
                <span className="flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full">
                  <Layers className="w-3 h-3" />{" "}
                  {t("tokens.packCredits", "Pack Credits:")}{" "}
                  <span className="text-white">{packCredits}</span>
                </span>
                <span className="flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full">
                  <Calendar className="w-3 h-3" />{" "}
                  {t("tokens.monthlyRemaining", "Monthly Left:")}{" "}
                  <span className="text-white">{monthlyRemaining}</span>
                </span>
              </div>
            </div>

            <div className="flex flex-col justify-end items-start md:items-end">
              <div className="text-right mb-1">
                <span className="text-xs text-gray-400 uppercase tracking-wider font-bold">
                  {t("tokens.yourPlan", "Current Plan")}
                </span>
                <div className="text-2xl font-bold text-primary-400 capitalize">
                  {planDetails?.name[language] || currentPlan}
                </div>
              </div>
              {subscriptionStatus && (
                <div className="mt-2 text-xs bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg text-gray-300">
                  {t("tokens.statusLabel", "Status:")}{" "}
                  {isCancelingSubscription
                    ? t("tokens.statusCanceling", "Canceling")
                    : t("tokens.statusActive", "Active")}
                </div>
              )}
              <div className="text-sm text-gray-400">
                {subscriptionData?.billing_cycle === "yearly"
                  ? t("tokens.billedYearly", "Billed Yearly")
                  : t("tokens.billedMonthly", "Billed Monthly")}
              </div>
              {getNextRenewalDate(subscriptionData) && (
                <div className="mt-4 text-xs bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg text-gray-300">
                  {isCancelingSubscription
                    ? t("tokens.cancelEffective", "Cancellation effective:")
                    : t("tokens.nextRenewal", "Next renewal:")}{" "}
                  {formatDate(getNextRenewalDate(subscriptionData))}
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Quick Stats Column */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm h-full flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-2 text-gray-500 font-medium">
              <div className="p-2 bg-green-50 text-green-600 rounded-lg">
                <TrendingUp className="w-5 h-5" />
              </div>
              {t("tokens.monthlyAllowance", "Monthly Allowance")}
            </div>
            <div className="text-3xl font-extrabold text-gray-900 ml-1">
              {monthlyCredits.toLocaleString()}
            </div>
            <div className="text-xs text-gray-400 ml-1 mt-1">
              {t("tokens.monthlyCredits", "Credits added every cycle")}
            </div>
          </div>

          {currentPlan !== "free" && (
            <button
              onClick={handleManageSubscription}
              className="w-full py-4 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-2xl text-sm font-bold transition-colors"
            >
              {t("tokens.cancelSubscription", "Cancel Subscription")}
            </button>
          )}
        </div>
      </div>

      {/* Buy Packs Section */}
      {pricingData && currentPlan !== "free" && (
        <div className="mt-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-primary-100 text-primary-600 rounded-lg">
              <ShoppingBag className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {t("tokens.topUpCredits")}
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {Object.entries(pricingData.additional_features.credit_packs).map(
              ([packKey, pack], i) => {
                const isLoading = loadingPack === packKey;
                const pricePerCredit = (pack.price_eur / pack.credits).toFixed(
                  3
                );

                return (
                  <motion.div
                    key={packKey}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 hover:shadow-lg hover:border-primary-200 transition-all group cursor-default"
                  >
                    <div className="text-xl sm:text-2xl font-extrabold text-primary-600 mb-1 group-hover:scale-105 transition-transform origin-left">
                      {pack.credits.toLocaleString()}
                    </div>
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                      {t("tokens.credits_label", "Credits")}
                    </div>

                    <div className="flex items-baseline gap-1 text-gray-900 font-bold text-lg">
                      €{pack.price_eur}
                    </div>
                    <div className="text-[10px] text-gray-400 mb-4">
                      €{pricePerCredit} / unit
                    </div>

                    <button
                      onClick={() => handleBuyCreditPack(packKey)}
                      disabled={isLoading}
                      className="w-full py-2 bg-gray-50 hover:bg-gray-900 hover:text-white text-gray-600 rounded-lg text-xs font-bold transition-all disabled:opacity-50"
                    >
                      {isLoading
                        ? t("tokens.processing", "Processing...")
                        : t("tokens.buyNow", "Buy Now")}
                    </button>
                  </motion.div>
                );
              }
            )}
          </div>
        </div>
      )}

      {/* Usage History (hidden for Spanish locale) */}
      {language !== "es" && (
        <div className="mt-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gray-100 text-gray-600 rounded-lg">
              <History className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              {t("tokens.usageHistory")}
            </h2>
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
            {usageHistory.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/50 border-b border-gray-100 text-xs uppercase text-gray-500 font-bold tracking-wider">
                      <th className="px-6 py-4">{t("tokens.date")}</th>
                      <th className="px-6 py-4">{t("tokens.activity")}</th>
                      <th className="px-6 py-4">{t("tokens.scanner")}</th>
                      <th className="px-6 py-4 text-right">
                        {t("tokens.cost")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {usageHistory.slice(0, 15).map((usage, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50/50 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-gray-600 font-medium">
                          {formatDate(usage.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                            {usage.document_name ||
                              t("tokens.document_processed")}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {usage.scanner_name || t("tokens.unknown_scanner")}
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                          -{usage.tokens_used || usage.credits_used || 1}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center text-gray-400">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>
                  {t(
                    "tokens.no_usage_history",
                    "No usage history available yet."
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.main>
  );
}
